import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Chip,
  Fade,
  Slide,
  LinearProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  alpha,
  useTheme,
  useMediaQuery,
  keyframes,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { usePreferencesWizard } from '../hooks/usePreferencesWizard';
import { WizardMessage } from '../types/preferencesWizard';

// Slide up transition for fullscreen dialog on mobile
const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Typing indicator animation
const typingDot = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

interface PreferencesWizardChatProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when wizard is completed with synthesized preferences */
  onComplete?: (preferences: string) => void;
  /** Callback when wizard is abandoned */
  onAbandon?: () => void;
  /** Custom max rounds for the wizard session */
  maxRounds?: number;
  /** Whether to auto-start the wizard when mounted */
  autoStart?: boolean;
}

/**
 * PreferencesWizardChat - AI-powered conversational interface for setting home preferences
 *
 * Displays a chat interface where users can have a conversation with an AI assistant
 * to articulate their home search preferences through natural dialogue.
 */
const PreferencesWizardChat: React.FC<PreferencesWizardChatProps> = ({
  open,
  onClose,
  onComplete,
  onAbandon,
  maxRounds,
  autoStart = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Swipe-to-close state for mobile
  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartY = useRef(0);
  const appBarRef = useRef<HTMLDivElement>(null);

  const {
    state,
    isLoading,
    error,
    activeSessionConflict,
    startWizard,
    sendMessage,
    completeWizard,
    abandonWizard,
    clearError,
    resumeExistingSession,
    startFreshSession,
  } = usePreferencesWizard();

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isAbandoning, setIsAbandoning] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showAbandonDialog, setShowAbandonDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [state.messages, scrollToBottom]);

  // Auto-start the wizard when dialog opens (if enabled and no active session)
  useEffect(() => {
    // Don't auto-start if there's a conflict - let user choose to resume or start fresh
    if (open && autoStart && !state.isActive && !isLoading && !error && !activeSessionConflict) {
      startWizard(maxRounds);
    }
  }, [open, autoStart, state.isActive, isLoading, error, activeSessionConflict, startWizard, maxRounds]);

  // Focus input after wizard starts
  useEffect(() => {
    if (open && state.isActive && !isLoading) {
      inputRef.current?.focus();
    }
  }, [open, state.isActive, isLoading]);

  // Swipe handlers for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;

    // Only allow downward swipes
    if (diff > 0) {
      setSwipeOffset(diff);
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    // If swiped down more than 100px, close the dialog
    if (swipeOffset > 100) {
      onClose();
    }
    setSwipeOffset(0);
  }, [swipeOffset, onClose]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending || isLoading) return;

    const message = inputValue.trim();
    setInputValue('');
    setIsSending(true);

    try {
      await sendMessage(message);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedResponse = (response: string) => {
    setInputValue(response);
    inputRef.current?.focus();
  };

  const handleSkipQuestion = async () => {
    if (isSending || isLoading) return;

    const skipMessage = "I'd rather skip this one for now";
    setIsSending(true);

    try {
      await sendMessage(skipMessage);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleOpenCompleteDialog = () => {
    setShowCompleteDialog(true);
  };

  const handleCloseCompleteDialog = () => {
    if (!isCompleting) {
      setShowCompleteDialog(false);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const preferences = await completeWizard();
      setShowCompleteDialog(false);
      if (preferences) {
        onComplete?.(preferences);
        onClose();
      }
    } finally {
      setIsCompleting(false);
    }
  };

  const handleOpenAbandonDialog = () => {
    setShowAbandonDialog(true);
  };

  const handleCloseAbandonDialog = () => {
    if (!isAbandoning) {
      setShowAbandonDialog(false);
    }
  };

  const handleAbandon = async () => {
    setIsAbandoning(true);
    try {
      const success = await abandonWizard();
      setShowAbandonDialog(false);
      if (success) {
        onAbandon?.();
        onClose();
      }
    } finally {
      setIsAbandoning(false);
    }
  };

  const handleRetry = () => {
    clearError();
    startWizard(maxRounds);
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  // Calculate progress percentage
  const progressPercent = state.maxRounds > 0
    ? Math.min((state.currentRound / state.maxRounds) * 100, 100)
    : 0;

  // Filter out system messages for display
  const visibleMessages = state.messages.filter(m => m.role !== 'system');

  const renderMessage = (message: WizardMessage, index: number) => {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';

    return (
      <Fade
        key={message.id}
        in
        timeout={300}
        style={{ transitionDelay: `${Math.min(index * 50, 200)}ms` }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start',
            mb: 2,
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              maxWidth: { xs: '85%', sm: '75%', md: '70%' },
              flexDirection: isUser ? 'row-reverse' : 'row',
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: isUser ? 'primary.main' : 'secondary.main',
                flexShrink: 0,
              }}
            >
              {isUser ? (
                <PersonIcon fontSize="small" />
              ) : (
                <AutoAwesomeIcon fontSize="small" />
              )}
            </Avatar>

            <Slide
              direction={isUser ? 'left' : 'right'}
              in
              timeout={300}
            >
              <Box
                sx={{
                  bgcolor: isUser
                    ? 'primary.main'
                    : 'background.paper',
                  color: isUser
                    ? 'primary.contrastText'
                    : 'text.primary',
                  borderRadius: isUser
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
                  p: { xs: 1.5, sm: 2 },
                  boxShadow: theme.shadows[1],
                  border: isAssistant
                    ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
                    : 'none',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.5,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  }}
                >
                  {message.content}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.7,
                    fontSize: '0.65rem',
                    display: 'block',
                    mt: 0.5,
                    textAlign: isUser ? 'right' : 'left',
                  }}
                >
                  {formatTime(message.createdAt)}
                </Typography>
              </Box>
            </Slide>
          </Box>
        </Box>
      </Fade>
    );
  };

  const renderTypingIndicator = () => (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          mb: 2,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'secondary.main',
          }}
        >
          <AutoAwesomeIcon fontSize="small" />
        </Avatar>
        <Box
          sx={{
            p: 1.5,
            px: 2,
            borderRadius: '16px 16px 16px 4px',
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            display: 'flex',
            gap: 0.5,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
                animation: `${typingDot} 1.4s infinite`,
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Fade>
  );

  const renderSuggestedResponses = () => {
    // Show skip option even when there are no suggested responses (but not during sending/loading)
    const hasSuggestions = (state.suggestedResponses?.length ?? 0) > 0;
    if (isSending || isLoading) return null;
    // Only show after at least one AI message (not on empty state)
    if (visibleMessages.length === 0) return null;

    return (
      <Fade in timeout={400}>
        <Box
          sx={{
            px: { xs: 1, sm: 2 },
            mb: 2,
          }}
        >
          {hasSuggestions && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, ml: 0.5 }}
            >
              Quick replies:
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {hasSuggestions && state.suggestedResponses?.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion}
                onClick={() => handleSuggestedResponse(suggestion)}
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    borderColor: 'primary.main',
                    transform: 'translateY(-1px)',
                    boxShadow: theme.shadows[2],
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              />
            ))}
            {/* Skip for now option */}
            <Chip
              icon={<SkipNextIcon fontSize="small" />}
              label="Skip for now"
              onClick={handleSkipQuestion}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                borderColor: alpha(theme.palette.text.secondary, 0.3),
                color: 'text.secondary',
                '& .MuiChip-icon': {
                  color: 'text.secondary',
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.text.secondary, 0.08),
                  borderColor: 'text.secondary',
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[1],
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            />
          </Box>
        </Box>
      </Fade>
    );
  };

  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        textAlign: 'center',
        flex: 1,
      }}
    >
      <Avatar
        sx={{
          width: 64,
          height: 64,
          bgcolor: 'secondary.main',
          mb: 2,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 32 }} />
      </Avatar>
      <Typography variant="h6" gutterBottom>
        Home Preferences Wizard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Have a conversation to discover your ideal home preferences.
        Our AI will help you articulate what you&apos;re looking for.
      </Typography>
      {!state.isActive && !isLoading && (
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => startWizard(maxRounds)}
        >
          Start Wizard
        </Button>
      )}
      {isLoading && <CircularProgress size={32} />}
    </Box>
  );

  const renderActiveSessionConflict = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 4,
        textAlign: 'center',
        flex: 1,
      }}
    >
      <Avatar
        sx={{
          width: 64,
          height: 64,
          bgcolor: alpha(theme.palette.warning.main, 0.2),
          mb: 2,
        }}
      >
        <WarningIcon sx={{ fontSize: 32, color: 'warning.main' }} />
      </Avatar>
      <Typography variant="h6" gutterBottom>
        Existing Session Found
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 360 }}
      >
        You have an unfinished preferences wizard session. Would you like to continue where you left off, or start a new conversation?
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={resumeExistingSession}
          disabled={isLoading}
        >
          Resume Session
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          onClick={() => startFreshSession(maxRounds)}
          disabled={isLoading}
        >
          Start Fresh
        </Button>
      </Box>
    </Box>
  );

  const renderError = () => {
    // Don't show error alert when activeSessionConflict is set (conflict UI shown in messages area)
    if (activeSessionConflict) {
      return null;
    }

    return (
      <Alert
        severity="error"
        action={
          <IconButton size="small" onClick={handleRetry}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        }
        sx={{ mx: 2, mb: 2 }}
      >
        {error}
      </Alert>
    );
  };

  // Mobile header with swipe-to-close AppBar
  const renderMobileHeader = () => (
    <AppBar
      position="static"
      color="primary"
      elevation={1}
      ref={appBarRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{
        // iOS safe area for notch
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <Toolbar sx={{ minHeight: '56px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: alpha('#fff', 0.2),
            }}
          >
            <AutoAwesomeIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              Preferences Wizard
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {Math.round(progressPercent)}% complete
            </Typography>
          </Box>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );

  // Desktop header with avatar and progress ring
  const renderDesktopHeader = () => (
    <Box
      sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Avatar with progress ring */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={progressPercent}
            size={44}
            thickness={3}
            sx={{
              color: 'primary.main',
              position: 'absolute',
              left: -2,
              top: -2,
            }}
          />
          <CircularProgress
            variant="determinate"
            value={100}
            size={44}
            thickness={3}
            sx={{
              color: alpha(theme.palette.primary.main, 0.15),
              position: 'absolute',
              left: -2,
              top: -2,
            }}
          />
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'secondary.main',
            }}
          >
            <AutoAwesomeIcon />
          </Avatar>
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            Preferences Wizard
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Finding your perfect home
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {state.isActive && (
          <>
            <IconButton
              size="small"
              color="success"
              onClick={handleOpenCompleteDialog}
              disabled={isLoading || isSending || isCompleting || isAbandoning}
              title="Complete wizard and save preferences"
            >
              <CheckCircleIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={handleOpenAbandonDialog}
              disabled={isLoading || isSending || isCompleting || isAbandoning}
              title="Cancel wizard without saving"
            >
              <CancelIcon />
            </IconButton>
          </>
        )}
        <IconButton
          size="small"
          onClick={onClose}
          title="Close"
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );

  const renderHeader = () => (isMobile ? renderMobileHeader() : renderDesktopHeader());

  const renderProgress = () => {
    return (
      <Box>
        {/* Main progress bar - clean and simple */}
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 4,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiLinearProgress-bar': {
              transition: 'transform 0.4s ease-in-out',
            },
          }}
        />
      </Box>
    );
  };

  const isActionInProgress = isLoading || isSending || isCompleting || isAbandoning;

  // Determine if we're in "early exit" mode (after 2-3 exchanges but before substantial progress)
  // An exchange = 1 user message + 1 assistant response (pair of messages)
  const userMessageCount = visibleMessages.filter(m => m.role === 'user').length;
  // Show action buttons after at least 1 complete exchange (user has responded at least once)
  const hasMinimalProgress = visibleMessages.length >= 2; // At least AI intro + 1 user response
  // Show encouraging early exit message when in the early phase (1-3 user responses)
  const isEarlyExit = userMessageCount >= 1 && userMessageCount <= 3;

  const renderActionButtons = () => {
    // Show action buttons when at least one conversation exchange has occurred
    if (!state.isActive || !hasMinimalProgress) return null;

    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: alpha(theme.palette.divider, 0.5),
            bgcolor: alpha(theme.palette.background.paper, 0.9),
          }}
        >
          {/* Positive messaging for early exit */}
          {isEarlyExit && (
            <Typography
              variant="caption"
              color="success.main"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 14 }} />
              We have enough to get started! You can always add more later.
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              color="error"
              size={isMobile ? 'small' : 'medium'}
              startIcon={isAbandoning ? <CircularProgress size={16} color="inherit" /> : <CloseIcon />}
              onClick={handleOpenAbandonDialog}
              disabled={isActionInProgress}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                minWidth: { xs: 100, sm: 120 },
              }}
            >
              {isAbandoning ? 'Cancelling...' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              color="success"
              size={isMobile ? 'small' : 'medium'}
              startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleOpenCompleteDialog}
              disabled={isActionInProgress}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                minWidth: { xs: 100, sm: 120 },
              }}
            >
              {isCompleting ? 'Saving...' : 'Finish & Save'}
            </Button>
          </Box>
        </Box>
      </Fade>
    );
  };

  const renderCompleteDialog = () => {
    // Determine messaging based on completion stage
    const isEarlyCompletion = isEarlyExit;

    return (
      <Dialog
        open={showCompleteDialog}
        onClose={handleCloseCompleteDialog}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 3001 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          {isEarlyCompletion ? 'Ready to Get Started!' : 'Complete Preferences Wizard'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {isEarlyCompletion
              ? "Great progress! We have enough information to start finding homes that match your preferences."
              : "Are you ready to save your home preferences?"
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEarlyCompletion
              ? "Alex will use what you've shared to begin personalizing your experience. You can always come back to add more details later!"
              : "The AI will analyze your conversation and generate a comprehensive profile of your home search criteria. You can always update your preferences later."
            }
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <Typography variant="body2" fontWeight="medium" color="success.dark">
              Your preferences will be saved to your profile and used to:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              <li>Personalize your home search results</li>
              <li>Help your agent understand your needs</li>
              <li>Provide context for AI-powered features</li>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseCompleteDialog}
            disabled={isCompleting}
          >
            {isEarlyCompletion ? 'Keep Chatting' : 'Continue Chatting'}
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            color="success"
            disabled={isCompleting}
            startIcon={isCompleting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
          >
            {isCompleting ? 'Saving Preferences...' : (isEarlyCompletion ? 'Save & Get Started' : 'Save Preferences')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderAbandonDialog = () => (
    <Dialog
      open={showAbandonDialog}
      onClose={handleCloseAbandonDialog}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 3001 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Cancel Preferences Wizard
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to cancel the preferences wizard?
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <Typography variant="body2" color="warning.dark" fontWeight="medium">
            This will discard your current conversation and any preferences you&apos;ve discussed.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can start a new wizard session at any time from your profile page.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleCloseAbandonDialog}
          disabled={isAbandoning}
        >
          Continue Chatting
        </Button>
        <Button
          onClick={handleAbandon}
          variant="contained"
          color="error"
          disabled={isAbandoning}
          startIcon={isAbandoning ? <CircularProgress size={18} color="inherit" /> : <CancelIcon />}
        >
          {isAbandoning ? 'Cancelling...' : 'Discard & Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderInput = () => (
    <Box
      sx={{
        p: 2,
        pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 8px)' : 2,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type your message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!state.isActive || isActionInProgress}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              '&:hover': {
                '& > fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!inputValue.trim() || !state.isActive || isActionInProgress}
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&:disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500',
            },
            width: 40,
            height: 40,
          }}
        >
          {isSending ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );

  // Content that goes inside the dialog
  const renderContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        // Mobile swipe animation
        ...(isMobile && {
          transform: `translateY(${swipeOffset}px)`,
          transition: swipeOffset ? 'none' : 'transform 0.3s ease',
          opacity: Math.max(1 - swipeOffset / 200, 0.5),
        }),
      }}
    >
      {/* Header */}
      {state.isActive && renderHeader()}

      {/* Progress bar */}
      {state.isActive && renderProgress()}

      {/* Error display */}
      {error && renderError()}

      {/* Messages area */}
      <Box
        aria-live="polite"
        aria-busy={isLoading}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 2,
          bgcolor: alpha(theme.palette.grey[100], 0.3),
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {visibleMessages.length === 0 && !isLoading ? (
          activeSessionConflict ? renderActiveSessionConflict() : renderEmptyState()
        ) : (
          <>
            {visibleMessages.map((message, index) => renderMessage(message, index))}
            {(isSending || isLoading) && state.isActive && renderTypingIndicator()}
            {renderSuggestedResponses()}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Action buttons */}
      {renderActionButtons()}

      {/* Input area */}
      {state.isActive && renderInput()}

      {/* Completion state */}
      {state.isComplete && (
        <Box
          sx={{
            p: 3,
            pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 24px)' : 3,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.1),
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Wizard Complete!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your preferences have been saved to your profile.
          </Typography>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ mt: 2 }}
          >
            Done
          </Button>
        </Box>
      )}

      {/* Confirmation dialogs */}
      {renderCompleteDialog()}
      {renderAbandonDialog()}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      TransitionComponent={isMobile ? SlideUpTransition : undefined}
      sx={{
        zIndex: 2100,
        // Desktop dialog styling
        ...(!isMobile && {
          '& .MuiDialog-paper': {
            height: '80vh',
            maxHeight: '700px',
            borderRadius: 2,
          },
        }),
      }}
      PaperProps={{
        sx: {
          // Mobile fullscreen styling
          ...(isMobile && {
            margin: 0,
            maxHeight: '100%',
            width: '100%',
            maxWidth: '100%',
          }),
        },
      }}
    >
      {renderContent()}
    </Dialog>
  );
};

export default PreferencesWizardChat;
